import React from 'react';
import { ChevronLeft, Pencil, Check } from 'lucide-react';
import { ItineraryItem, TripMember } from '../../types/trip';
import { useDesign } from '../../designs';
import { updateItineraryItem, deleteItineraryItem, setRsvp } from '../../services/itinerary';
import { getDisplayMembers } from '../../utils/members';
import DesignButton from '../../components/ui/DesignButton';
import { formatTime12h } from '../../components/ui/TimeSpinner';
import { parseStreetAddress, parseVenueName } from '../../utils/address';
import SinglePinMap from '../../components/ui/SinglePinMap';

interface ItemDetailViewProps {
  item: ItineraryItem;
  members: Record<string, TripMember>;
  tripId: string;
  isPlanner: boolean;
  canEdit: boolean;
  currentUid: string;
  claimedMemberUid: string | null;
  onBack: () => void;
  onEdit: () => void;
}

export default function ItemDetailView({
  item, members, tripId, isPlanner, canEdit, currentUid, claimedMemberUid, onBack, onEdit,
}: ItemDetailViewProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const isConfirmed = item.status === 'confirmed';
  const allMembers = getDisplayMembers(members);
  const rsvpCount = item.rsvps ? Object.values(item.rsvps).filter(Boolean).length : 0;
  const streetAddress = parseStreetAddress(item.address || '');
  // Only activities get a separate venue line — for dining, the item name IS the venue.
  // Prefer the persisted venue field, fall back to parsing from address for legacy items.
  const venueName = item.type === 'activity'
    ? (item.venue || parseVenueName(item.address || ''))
    : '';

  const handleToggleStatus = async () => {
    await updateItineraryItem(tripId, item.itemId, { status: isConfirmed ? 'considering' : 'confirmed' });
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    await deleteItineraryItem(tripId, item.itemId);
    onBack();
  };

  const handleToggleRsvp = async (uid: string) => {
    const current = item.rsvps?.[uid] || false;
    await setRsvp(tripId, item.itemId, uid, !current);
  };

  const dayDate = item.day
    ? new Date(item.day + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : '';

  const hasMapData = (item.lat != null && item.lng != null) || !!item.address;

  return (
    <div style={{ padding: '16px 26px' }}>
      {/* Header — Back / Edit */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '4px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 400,
          color: d.textSecondary,
        }}>
          <ChevronLeft size={16} strokeWidth={1.5} color={d.textSecondary} />
          Back
        </button>
        {canEdit && (
          <button onClick={onEdit} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '4px',
            fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 400,
            color: d.textSecondary,
          }}>
            Edit
            <Pencil size={14} strokeWidth={1.5} color={d.textSecondary} />
          </button>
        )}
      </div>

      {/* Status line — label, not a button */}
      <div style={{ marginBottom: '14px' }}>
        {isConfirmed ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
            letterSpacing: '3.5px', textTransform: 'uppercase',
            color: d.confirmed,
          }}>
            <Check size={12} strokeWidth={2.5} color={d.confirmed} />
            Confirmed
          </span>
        ) : (
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 300,
            fontStyle: 'italic', letterSpacing: '3.5px', textTransform: 'uppercase',
            color: d.textSecondary,
          }}>
            Considering
          </span>
        )}
      </div>

      {/* Name — tappable if URL exists */}
      {item.url ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
          display: 'block', fontFamily: displayFont, fontSize: fs.screenHeader, fontWeight: 700,
          color: d.accent, margin: '0 0 6px', textDecoration: 'none', lineHeight: 1.1,
        }}>
          {item.name} →
        </a>
      ) : (
        <h2 style={{
          fontFamily: displayFont, fontSize: fs.screenHeader, fontWeight: 700,
          color: d.textPrimary, margin: '0 0 6px', lineHeight: 1.1,
        }}>
          {item.name}
        </h2>
      )}

      {/* Venue (activities only) — Bodoni italic, gold */}
      {venueName && (
        <p style={{
          fontFamily: displayFont, fontSize: fs.bodyInput, fontWeight: 400,
          fontStyle: 'italic', color: d.accent, margin: '0 0 4px',
        }}>
          {venueName}
        </p>
      )}

      {/* Street address */}
      {streetAddress && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
          color: d.textSecondary, margin: '0 0 6px',
        }}>
          {streetAddress}
        </p>
      )}

      {/* Date/time */}
      {(item.time || dayDate) && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
          color: d.textSecondary, margin: '0 0 10px',
        }}>
          {item.time && formatTime12h(item.time)}
          {item.time && item.endTime && ` – ${formatTime12h(item.endTime)}`}
          {item.time && dayDate && ' · '}
          {dayDate}
        </p>
      )}

      {/* Notes */}
      {item.notes && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary, margin: '0 0 20px',
        }}>
          {item.notes}
        </p>
      )}

      {/* Location section */}
      {(streetAddress || hasMapData) && (
        <div style={{ marginTop: '24px', marginBottom: '28px' }}>
          <div style={{ height: '1px', backgroundColor: d.border, marginBottom: '14px' }} />
          <h4 style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
            letterSpacing: '3.5px', textTransform: 'uppercase',
            color: d.textSecondary, margin: '0 0 10px',
          }}>Location</h4>
          {streetAddress && (
            <p style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
              color: d.textPrimary, margin: '0 0 12px',
            }}>{streetAddress}</p>
          )}
          {hasMapData && (
            <SinglePinMap lat={item.lat} lng={item.lng} address={item.address} height={180} />
          )}
        </div>
      )}

      {/* Who's In */}
      <div>
        <div style={{ height: '1px', backgroundColor: d.border, marginBottom: '14px' }} />
        <h4 style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3.5px', textTransform: 'uppercase',
          color: d.textSecondary, margin: '0 0 14px',
        }}>
          Who's In · {rsvpCount} of {allMembers.length}
        </h4>

        {allMembers.map((member) => {
          const isGoing = item.rsvps?.[member.uid] || false;
          const canToggle = isPlanner || (canEdit && member.uid === claimedMemberUid);

          return (
            <div
              key={member.uid}
              onClick={() => canToggle && handleToggleRsvp(member.uid)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: `1px solid rgba(255,255,255,0.06)`,
                cursor: canToggle ? 'pointer' : 'default',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  border: `1px solid ${isGoing ? d.accent : d.textTertiary}`,
                  backgroundColor: isGoing ? 'rgba(200,146,58,0.15)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: fs.detail, color: isGoing ? d.accent : d.textTertiary,
                }}>
                  {isGoing ? '✓' : ''}
                </div>
                <span style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.body,
                  fontWeight: isGoing ? 500 : 300,
                  color: isGoing ? d.textPrimary : d.textSecondary,
                }}>
                  {member.displayName}
                </span>
              </div>
              {isGoing && (
                <span style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 400,
                  letterSpacing: '1px', textTransform: 'uppercase', color: d.accent,
                }}>Going</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Status change + Delete — all destructive/state actions live at the bottom */}
      {isPlanner && (
        <div style={{ marginTop: '32px' }}>
          <button onClick={handleToggleStatus} type="button" style={{
            display: 'block', width: '100%', marginBottom: '12px', padding: '8px',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: "'Raleway', sans-serif",
            fontSize: fs.bodyDetail,
            fontWeight: isConfirmed ? 400 : 700,
            letterSpacing: isConfirmed ? 'normal' : '1.5px',
            textTransform: isConfirmed ? 'none' : 'uppercase',
            color: isConfirmed ? d.textSecondary : d.confirmed,
            textAlign: 'center',
          }}>
            {isConfirmed ? 'Move to considering' : '◆  Confirm this plan  ◆'}
          </button>
          <DesignButton tier={2} fullWidth onClick={handleDelete}
            style={{ color: d.error, borderColor: d.error }}>
            Delete
          </DesignButton>
        </div>
      )}
    </div>
  );
}
